import React from 'react';
import { getImageProps } from 'next/image';
import Script from 'next/script';
import Stack from './contentstack/stack';
import { contentstackBlockGroups } from '@/lib/constants/contentstackBlockGroups.mjs';

export const currencyCode = ({ code }) => {
  let currencySymbol = '$';
  if (code == 'USD') {
    currencySymbol = '$';
  }
  if (code == 'CAD') {
    currencySymbol = 'CAD';
  }

  return currencySymbol;
};

export const createUrl = (pathname, params) => {
  const paramsString = params.toString();
  const queryString = `${paramsString.length ? '?' : ''}${paramsString}`;

  return `${pathname}${queryString}`;
};

export const ensureStartsWith = (stringToCheck, startsWith) =>
  stringToCheck.startsWith(startsWith)
    ? stringToCheck
    : `${startsWith}${stringToCheck}`;

export const validateEnvironmentVariables = () => {
  const requiredEnvironmentVariables = [
    'SHOPIFY_STORE_DOMAIN',
    'SHOPIFY_STOREFRONT_ACCESS_TOKEN',
  ];
  const missingEnvironmentVariables = [];

  requiredEnvironmentVariables.forEach((envVar) => {
    if (!process.env[envVar]) {
      missingEnvironmentVariables.push(envVar);
    }
  });

  if (missingEnvironmentVariables.length) {
    throw new Error(
      `The following environment variables are missing. Your site will not work without them. Read more: https://vercel.com/docs/integrations/shopify#configure-environment-variables\n\n${missingEnvironmentVariables.join(
        '\n'
      )}\n`
    );
  }

  if (
    process.env.SHOPIFY_STORE_DOMAIN?.includes('[') ||
    process.env.SHOPIFY_STORE_DOMAIN?.includes(']')
  ) {
    throw new Error(
      'Your `SHOPIFY_STORE_DOMAIN` environment variable includes brackets (ie. `[` and / or `]`). Your site will not work with them there. Please remove them.'
    );
  }
};

export async function fetchLinkReferenceData(_content_type_uid, uid) {
  try {
    const query = Stack.ContentType(_content_type_uid).Entry(uid);
    const response = await query.toJSON().fetch();

    return response;
  } catch (error) {
    console.error(
      `Error fetching content for UID: ${uid}, content type: ${_content_type_uid}`,
      error
    );
    return null;
  }
}

/**
 * Fetches all entries for a given content type from ContentStack
 * @param contentType The ContentStack content type to fetch (e.g., 'article', 'page', 'product')
 * @param options Optional parameters for the query
 * @returns Array of entries for the specified content type
 */
export async function fetchAllEntries(contentType, options = {}) {
  const {
    orderBy = 'created_at',
    orderDirection = 'desc',
    includeDimensions = true,
    limit = null,
  } = options;

  try {
    const query = Stack.ContentType(contentType).Query();

    // Add ordering using the correct ContentStack SDK method
    if (orderDirection === 'desc') {
      query.descending(orderBy);
    } else {
      query.ascending(orderBy);
    }

    // Add dimensions if requested
    if (includeDimensions) {
      query.addParam('include_dimension', 'true');
    }

    // Add limit if specified
    if (limit) {
      query.limit(limit);
    }

    const result = await query.toJSON().find();

    const entries = result?.[0] || [];

    return entries;
  } catch (error) {
    console.error(
      `Error fetching all entries for content type: ${contentType}`,
      error
    );
    return [];
  }
}

export async function fetchEntryByUid(contentType, uid, options = {}) {
  const { includeDimension = true, only = [] } = options;

  try {
    // Handle multiple UIDs
    if (Array.isArray(uid)) {
      const query = Stack.ContentType(contentType).Query();

      query.containedIn('uid', uid);

      // Limit the fields returned to only those specified in the only array
      if (only.length) {
        query.only(only);
      }

      // Add dimensions if requested
      if (includeDimension) {
        query.addParam('include_dimension', 'true');
      }

      const result = await query.toJSON().find();
      return result?.[0] || [];
    }

    // Handle single UID
    const query = Stack.ContentType(contentType).Entry(uid);

    // Limit the fields returned to only those specified in the only array
    if (only.length) {
      query.only(only);
    }

    // Add dimensions if requested
    if (includeDimension) {
      query.addParam('include_dimension', 'true');
    }

    const entry = await query.toJSON().fetch();

    return entry;
  } catch (error) {
    console.error(
      `Error fetching entry by UID: ${uid}, content type: ${contentType}`,
      error
    );
    return Array.isArray(uid) ? [] : null;
  }
}

/**
 * Returns alt text for a Contentstack image, preferring the description, but
 * falling back to the filename without the file extension.
 * @param image A Contentstack image object (object with title and description)
 */
export function resolveImageAltText(image) {
  return image.description || image.title.replace(/\.[^/.]+$/, '');
}

/** Contentstack Image Delivery API — https://www.contentstack.com/docs/developers/apis/image-delivery-api */
const CONTENTSTACK_IMAGE_PARAMS = 'auto=webp&disable=upscale';

export function buildContentstackImageUrl(url, maxWidth) {
  if (!url) return url;
  const separator = url.includes('?') ? '&' : '?';
  const widthParam = maxWidth ? `width=${maxWidth}&` : '';
  return `${url}${separator}${widthParam}${CONTENTSTACK_IMAGE_PARAMS}`;
}

/**
 * Generates the srcSet and remaining next/image properties for a given
 * Contentstack image to be used in a picture element for art direction.
 * @param image A Contentstack image object (object with url, and dimension)
 * @param priority Whether the image is above the fold
 * @param quality The quality of the image
 * @param sizes Sizes where the image will be displayed (optional, but greatly
 *   affects performance for some images)
 * @returns An object containing the srcSet and remaining next/image properties
 */
export const getContentImageProps = ({
  image,
  priority = 'false',
  quality = 75,
  sizes,
}) => {
  // If missing needed image properties, return empty values that will allow
  // <picture> tags to gracefully handle missing sources (but output a warning).
  if (!image?.url || !image?.dimension) {
    console.warn('Missing image properties:', image);
    return { srcSet: '', remainingProps: {} };
  }

  const {
    props: { srcSet, ...remainingProps },
  } = getImageProps({
    src: buildContentstackImageUrl(image.url),
    width: image.dimension.width,
    height: image.dimension.height,
    quality,
    priority,
    sizes,
  });
  return { srcSet, remainingProps };
};

export function CustomGoogleTagManager({ gtmId }) {
  if (!gtmId || typeof gtmId !== 'string' || !gtmId.trim()) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        '[Elevar] NEXT_PUBLIC_ELEVAR_GTM_ID is unset — GTM will not load. ElevarDataLayer events still push but no tags fire.',
      );
    }
    return null;
  }

  const id = gtmId.trim();
  const safeScriptId = `gtm-script-${id}`;
  const safeIframeId = `gtm-iframe-${id}`;

  return (
    <>
      <Script
        id={safeScriptId}
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${id}');
          `,
        }}
      />
      <noscript>
        <iframe
          src={`https://www.googletagmanager.com/ns.html?id=${id}`}
          height="0"
          width="0"
          style={{ display: 'none', visibility: 'hidden' }}
          title={`GTM ${id}`}
          id={safeIframeId}
        />
      </noscript>
    </>
  );
}

const GROUPED_BLOCK_KEYS = new Set(
  contentstackBlockGroups.map((group) => group.value),
);

export function hasBlockType(blocks, contentTypeUid) {
  return Array.isArray(blocks) && blocks.some((block) =>
    block[contentTypeUid] !== undefined ||
    Object.keys(block).some((key) =>
      GROUPED_BLOCK_KEYS.has(key) &&
      Array.isArray(block[key]?.reference) &&
      block[key].reference.some((ref) => ref?._content_type_uid === contentTypeUid)
    )
  );
}
